import React, {useContext, useEffect, useRef, useState} from 'react';
import {useQuery} from "@tanstack/react-query";
import {AgGridReact} from "ag-grid-react";
import {v4 as uuidv4} from "uuid";
import {AgentContext} from "../../context/agent-context.jsx";
import GenericEditRemoveCellRenderer from "./generic-edit-remove-cell-renderer.jsx";
import CellRendererAttach from "../../utils/cell-renderer-attach.jsx";
import ConfirmationDeleteList from "../../../../../../components/utils/confirmation-delete-lists.jsx";
import {FaPlus} from "react-icons/fa";
import Swal from "sweetalert2";
import projectUtils from "../../../../../../utils/project-utils.js";
import {Card} from "../../../../../../components/card/card.jsx";
import Button from "../../../../../../components/button/button.jsx";
import aggridUtils from "../../../../../../utils/aggrid-utils.js";
import {Modal} from "../../../../../../components/modal/modal.jsx";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import InputDatetime from "../../../../../../components/input/input-datetime.jsx";
import Attach from "../../../../../../components/attach/attach.jsx";
import optionsUtils from "../../../../../../utils/options-utils.js";
import {format, parseISO, isAfter} from "date-fns";
import AbsenceTypeService from "../../../../../../services/web/absence-type-service.js";
import InputTextarea from "../../../../../../components/input/input-textarea.jsx";

const DayOff = () => {
    const generateInitialData = () => ({
        itemId: uuidv4(),
        id: null,
        typeId: null,
        typeName: "",
        startDate: "",
        endDate: "",
        attachId: "",
        observation: ""
    });

    const gridRef = useRef();
    const {
        user,
        setUser,
        setChanges,
        setTabSelected,
        setCpfFocus,
        tablesAttachs,
        setTablesAttachs
    } = useContext(AgentContext)
    const [data, setData] = useState(generateInitialData)
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [removedFile, setRemovedFile] = useState(null);
    const {data: absenceTypes = []} = useQuery({queryKey: ["absence-types"], queryFn: async () => (await AbsenceTypeService.list()).map(({id, name}) => ({value: id, label: name}))});

    useEffect(() => {
        if (open) {
            const attachFind = tablesAttachs.find(item => item.name?.includes(`dayOff-${data.itemId}`));
            if (attachFind) setFile(attachFind);
        }
    }, [open]);

    const remove = (data) => {
        ConfirmationDeleteList(() => {
            setUser(prev => ({
                ...prev,
                dayOff: prev.dayOff.filter(dep => dep.itemId !== data.itemId)
            }));

            setTablesAttachs((prevItems) => prevItems.filter((item) => !item.name.includes(`dayOff-${data.itemId}`)))
            setChanges('dayOff');
        })
    }

    const [columnDefs] = useState([
        {
            headerName: "Motivo", field: "typeName", flex: 1, comparator: aggridUtils.nullLastComparator
        },
        { headerName: "Observação",  field: "observation", flex: 1, comparator: aggridUtils.nullLastComparator},
        {
            headerName: "Data inicial", field: "startDate",
            valueFormatter: ({value}) => {
                if (value) return projectUtils.formatDate(value + 'T00:00:00', 'DD/MM/YYYY');

                return null;
            }, comparator: aggridUtils.nullLastComparator
        },
        {
            headerName: "Data final", field: "endDate", sort: "desc",
            valueFormatter: ({value}) => {
                if (value) return projectUtils.formatDate(value + 'T00:00:00', 'DD/MM/YYYY');

                return null;
            }, comparator: aggridUtils.nullLastComparator
        },
        {
            headerName: "Anexo", field: "", width: 60,
            cellRenderer: ({data}) => {
                const {attachId, itemId} = data;
                return <CellRendererAttach attachId={attachId} itemId={itemId} target={"dayOff"}/>
            }
        },
        {
            headerName: "", field: "", width: 100,
            cellRenderer: GenericEditRemoveCellRenderer,
            cellRendererParams: {setData, setOpen, remove}
        },
    ]);

    const handleDate = (id, value) => {
        const dateFormated = value ? format(value, "yyyy-MM-dd") : null
        setData(prev => ({...prev, [id]: dateFormated}))
    };

    const handleInputs = ({target: {value, id}}) => setData(prev => ({...prev, [id]: value}));

    const save = () => {
        let message = ""
        if (!data.typeId) message = "O campo \"Motivo\" é obrigatório.\n"
        if (!data.endDate) message = "O campo \"Final\" é obrigatório.\n"
        if (!data.startDate) message = "O campo \"Inicial\" é obrigatório.\n"
        const startDateISO = parseISO(data.startDate);
        const endDateISO = parseISO(data.endDate);

        if (isAfter(startDateISO, endDateISO)) message = "Data inicial não pode ser maior que final.\n"
        if (message) {
            projectUtils.swalErrorMessage("", message, "Ok");
            return
        }

        setUser(prevItems => {
            const exists = prevItems.dayOff.some(t => t.itemId === data.itemId);

            const updatedTrainings = exists
                ? prevItems.dayOff.map(t => (t.itemId === data.itemId ? data : t))
                : [...prevItems.dayOff, data];

            return {
                ...prevItems,
                dayOff: updatedTrainings
            };
        });

        if (file) {
            setTablesAttachs(prevItems => {
                const exists = prevItems.some(item => item.name === file.name);
                return exists ? prevItems.map(item => item.name === file.name ? file : item) : [...prevItems, file];
            });
        }

        if (removedFile) setTablesAttachs((prevItems) => prevItems.filter((item) => item.name !== removedFile.name));
        setChanges('dayOff');

        onHide();
    }

    const onHide = () => {
        setData(generateInitialData());
        setOpen(false)
        setFile(null)
        setRemovedFile(null)
    }

    const onChangeFile = (uploadedFile) => {
        if (uploadedFile) setFile(uploadedFile);
    }

    const removeFile = (uploadedFile) => {
        if (uploadedFile) {
            setRemovedFile(uploadedFile);

            const target = "attachId"
            setData(prev => ({...prev, [target]: null}));
        }
    }

    const handleClick = () => {

        if (!user.general.cpf) {
            Swal.fire({
                title: "Atenção",
                text: "Informe o CPF para incluir ausências.",
                icon: "warning",
                confirmButtonText: "Continuar",
                confirmButtonColor: "#063A6C",
                cancelButtonText: "Cancelar",
                showCancelButton: true,
                allowOutsideClick: false,
                allowEscapeKey: false,
            }).then(({isConfirmed}) => {
                if (isConfirmed) {
                    setTabSelected("general")
                    setCpfFocus(true)
                }
            });
        } else {
            setOpen(true)
        }
    }

    return (
        <>
            <Card>
                <Card.Header className={"gap-2"}>
                    <Card.Title>Ausências</Card.Title>
                    <Button className="h-7.5! w-7.5 !p-0" onClick={handleClick}>
                        <FaPlus/>
                    </Button>
                </Card.Header>
                <Card.Body>
                    <div className={"h-[calc(100vh-196px)]"}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={user.dayOff}
                            columnDefs={columnDefs}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={aggridUtils.generalTheme()}
                        />
                    </div>
                </Card.Body>
            </Card>

            <Modal overflow={true} isCloseClick={false} open={open} setOpen={onHide}  title={data.itemId ? "Editar ausência" : "Adicionar ausência"} className={"w-4/12"}>
                <Modal.Body className="p-2">
                    <div className="grid grid-cols-12 gap-4 mb-3">
                        <div className="col-span-12 ">
                            <SelectComponent
                                id="typeId"
                                value={absenceTypes.find(option => option.value === data.typeId) ?? null}
                                options={absenceTypes}
                                label={"Motivo *"}
                                onChange={(selected) => setData(prev => ({...prev, typeId: selected?.value ?? null, typeName: selected?.label ?? ""}))}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mb-3" style={{height: "35px"}}>
                        <div className={"col-span-6"}>
                            <div className="form-outline-custom">
                                <InputDatetime
                                    id={"startDate"}
                                    selected={data.startDate}
                                    label={"Inicial *"}
                                    dateFormat={"L"}
                                    onChange={(date) => handleDate("startDate", date)}
                                />
                            </div>
                        </div>
                        <div className={"col-span-6"}>
                            <div className="form-outline-custom">
                                <InputDatetime
                                    id={"endDate"}
                                    selected={data.endDate}
                                    label={"Final *"}
                                    dateFormat={"L"}
                                    onChange={(date) => handleDate("endDate", date)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-span-12 gap-4 mb-3">
                        <InputTextarea id="observation" value={data.observation} label="Observação" style={{height: "250px"}} onChange={handleInputs}/>
                    </div>
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-3">
                            <Attach onChangeFile={onChangeFile} removeFile={removeFile} itemId={`dayOff-${data.itemId}`}
                                    attachedFile={file} attachId={data.attachId}
                            />
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer className={"p-1"}>
                    <div className={"w-full flex justify-end"}>
                        <Button onClick={save}>
                            Confirmar
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default DayOff;